import { GET_LEARNING_DEVELOPMENT_POSTS } from "../../keys";
import {
	infiniteQueryOptions,
	QueryClient,
	useInfiniteQuery,
	useQuery,
} from "@tanstack/react-query";
import {
	getLearningAndDevelopmentPosts,
	LearningAndDevelopmentPostQueryFilter,
} from "@onair/services/learning-development";

export const useGetLearningAndDevelopmentPostsQuery = (options: {
	query?: LearningAndDevelopmentPostQueryFilter;
	enabled?: boolean;
}) => {
	const { query, enabled = true } = options;
	return useQuery({
		queryKey: [GET_LEARNING_DEVELOPMENT_POSTS, query],
		queryFn: () => getLearningAndDevelopmentPosts(query),
		enabled,
	});
};

export const prefetchLearningAndDevelopmentPosts = async (
	query: LearningAndDevelopmentPostQueryFilter,
	queryClient: QueryClient,
) => {
	await queryClient.prefetchQuery({
		queryKey: [GET_LEARNING_DEVELOPMENT_POSTS, query],
		queryFn: () => getLearningAndDevelopmentPosts(query),
	});
};

function learningAndDevelopmentInfiniteQueryOptions(
	filters: LearningAndDevelopmentPostQueryFilter,
) {
	let queryKey: (string | LearningAndDevelopmentPostQueryFilter)[] = [
		GET_LEARNING_DEVELOPMENT_POSTS,
	];
	let filterKeysValid: LearningAndDevelopmentPostQueryFilter = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof LearningAndDevelopmentPostQueryFilter]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [...queryKey, filterKeysValid];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: 1,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam = 1 }) =>
			getLearningAndDevelopmentPosts({ ...filters, page: pageParam }),
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.meta?.pagination?.pageCount === filters.page
				? undefined
				: lastPage.meta?.pagination?.page
					? lastPage.meta?.pagination?.page + 1
					: lastPage.meta?.pagination?.page;
		},
		getPreviousPageParam: (firstPage, allPages) => {
			return firstPage.meta?.pagination?.pageCount === filters.page
				? undefined
				: firstPage.meta?.pagination?.page
					? firstPage.meta?.pagination?.page - 1
					: firstPage.meta?.pagination?.page;
		},
	});
}

export const prefetchLearningAndDevelopmentPostsInfinite = async (
	query: LearningAndDevelopmentPostQueryFilter,
	queryClient: QueryClient,
) => {
	await queryClient.prefetchInfiniteQuery(
		learningAndDevelopmentInfiniteQueryOptions(query),
	);
};
export const useGetLearningAndDevelopmentPostsInfiniteQuery = (
	query: LearningAndDevelopmentPostQueryFilter,
) => {
	return useInfiniteQuery(learningAndDevelopmentInfiniteQueryOptions(query));
};
